// Copyright 2025, University of Maryland, All Rights Reserved
import React from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar,
  LineChart, Line,
  PieChart, Pie, Cell,
  ResponsiveContainer
} from 'recharts';

interface ChartRendererProps {
  chartData: any;
}

/**
 * Enhanced ChartRenderer with robust error handling and data validation
 * Prevents UI crashes by validating all chart data before rendering
 * Shows helpful error messages when data is malformed
 */
export const ChartRenderer: React.FC<ChartRendererProps> = ({ chartData }) => {
  // Early validation: Check if chartData exists
  if (!chartData || typeof chartData !== 'object') {
    console.error('ChartRenderer: No chart data provided');
    return null;
  }

  // Early validation: Check if chart_type exists
  if (!chartData.chart_type) {
    console.error('ChartRenderer: Missing chart_type', chartData);
    return (
      <div className="my-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          ⚠️ Chart type not specified. Unable to render visualization.
        </p>
      </div>
    );
  }

  // Wrap rendering in error boundary
  try {
    switch (chartData.chart_type) {
      case 'radar':
        return <RadarChartRenderer data={chartData} />;
      case 'scatter':
        return <ScatterChartRenderer data={chartData} />;
      case 'bar':
      case 'grouped-bar':
      case 'stacked-bar':
        return <BarChartRenderer data={chartData} />;
      case 'line':
        return <LineChartRenderer data={chartData} />;
      case 'pie':
        return <PieChartRenderer data={chartData} />;
      case 'heatmap':
        return <HeatmapRenderer data={chartData} />;
      default:
        console.warn(`ChartRenderer: Unsupported chart type: ${chartData.chart_type}`);
        return (
          <div className="my-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              ⚠️ Unsupported chart type: "{chartData.chart_type}"
            </p>
          </div>
        );
    }
  } catch (error) {
    console.error('ChartRenderer: Rendering error', error, chartData);
    return (
      <div className="my-6 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-sm text-red-800">
          ❌ Failed to render {chartData.chart_type} chart
        </p>
        <p className="text-xs text-red-700 mt-1">
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }
};

/**
 * Validates that a value is an array with minimum length
 */
const validateArray = (data: any, fieldName: string, minLength = 1): any[] | null => {
  if (!Array.isArray(data)) {
    console.error(`${fieldName} must be an array, got:`, typeof data);
    return null;
  }
  if (data.length < minLength) {
    console.error(`${fieldName} must have at least ${minLength} items, got ${data.length}`);
    return null;
  }
  return data;
};

/**
 * Validates datasets structure
 */
const validateDatasets = (datasets: any, requiredFields: string[] = []): any[] | null => {
  const validated = validateArray(datasets, 'datasets');
  if (!validated) return null;
  
  for (let i = 0; i < validated.length; i++) {
    const dataset = validated[i];
    if (typeof dataset !== 'object' || dataset === null) {
      console.error(`Dataset ${i} must be an object`);
      return null;
    }
    
    for (const field of requiredFields) {
      if (!(field in dataset)) {
        console.error(`Dataset ${i} missing required field: ${field}`);
        return null;
      }
    }
  }
  
  return validated;
};

/**
 * Generates a color for a given index using HSL
 */
const getDefaultColor = (index: number): string => {
  return `hsl(${(index * 137) % 360}, 70%, 50%)`;
};

// Radar Chart Renderer
const RadarChartRenderer: React.FC<{ data: any }> = ({ data }) => {
  try {
    const { title = 'Radar Chart', labels, datasets } = data;

    // Validate required fields
    const validatedLabels = validateArray(labels, 'labels');
    if (!validatedLabels) return null;

    const validatedDatasets = validateDatasets(datasets, ['name', 'values']);
    if (!validatedDatasets) return null;

    // Validate that all datasets have same length as labels
    for (let i = 0; i < validatedDatasets.length; i++) {
      const dataset = validatedDatasets[i];
      const values = validateArray(dataset.values, `dataset[${i}].values`);
      if (!values) return null;
      
      if (values.length !== validatedLabels.length) {
        console.error(
          `Dataset ${i} length mismatch: ${values.length} values vs ${validatedLabels.length} labels`
        );
        return null;
      }
    }

    // Transform data for Recharts
    const chartData = validatedLabels.map((label: string, index: number) => {
      const point: any = { category: label };
      validatedDatasets.forEach((dataset: any) => {
        point[dataset.name] = dataset.values[index];
      });
      return point;
    });

    return (
      <div className="my-4">
        <h4 className="text-base font-semibold mb-2 text-gray-800">{title}</h4>
        <ResponsiveContainer width="100%" height={280}>
          <RadarChart data={chartData}>
            <PolarGrid />
            <PolarAngleAxis dataKey="category" />
            <PolarRadiusAxis angle={90} domain={[0, 5]} />
            <Tooltip />
            <Legend />
            {validatedDatasets.map((dataset: any, index: number) => (
              <Radar
                key={index}
                name={dataset.name}
                dataKey={dataset.name}
                stroke={dataset.color || getDefaultColor(index)}
                fill={dataset.color || getDefaultColor(index)}
                fillOpacity={0.3}
              />
            ))}
          </RadarChart>
        </ResponsiveContainer>
      </div>
    );
  } catch (error) {
    console.error('RadarChart rendering error:', error, data);
    return null;
  }
};

// Scatter Chart Renderer
const ScatterChartRenderer: React.FC<{ data: any }> = ({ data }) => {
  try {
    const { title = 'Scatter Chart', datasets } = data;

    // Validate datasets
    const validatedDatasets = validateDatasets(datasets, ['name', 'points']);
    if (!validatedDatasets) return null;

    // Validate points structure
    for (let i = 0; i < validatedDatasets.length; i++) {
      const dataset = validatedDatasets[i];
      const points = validateArray(dataset.points, `dataset[${i}].points`);
      if (!points) return null;
      
      for (let j = 0; j < points.length; j++) {
        const point = points[j];
        if (typeof point !== 'object' || !('x' in point) || !('y' in point)) {
          console.error(`Dataset ${i}, point ${j} must have 'x' and 'y' properties`);
          return null;
        }
      }
    }

    return (
      <div className="my-4">
        <h4 className="text-base font-semibold mb-2 text-gray-800">{title}</h4>
        <ResponsiveContainer width="100%" height={280}>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              dataKey="x"
              name="Problem Number"
              label={{ value: 'Problem Number', position: 'insideBottom', offset: -5 }}
            />
            <YAxis
              type="number"
              dataKey="y"
              name="Complexity Level"
              domain={[1, 5]}
              ticks={[1, 2, 3, 4, 5]}
              label={{ value: 'Complexity Level', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white p-2 border border-gray-300 rounded shadow">
                      <p className="font-semibold">{data.label || `Point ${data.x}`}</p>
                      <p className="text-sm">Level: {data.y}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            {validatedDatasets.map((dataset: any, index: number) => (
              <Scatter
                key={index}
                name={dataset.name}
                data={dataset.points}
                fill={dataset.color || getDefaultColor(index)}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    );
  } catch (error) {
    console.error('ScatterChart rendering error:', error, data);
    return null;
  }
};

// Bar Chart Renderer (supports simple, grouped, and stacked)
const BarChartRenderer: React.FC<{ data: any }> = ({ data }) => {
  try {
    const { title = 'Bar Chart', labels, values, colors, datasets, chart_type } = data;

    // Check if this is a simple bar chart (values) or grouped/stacked (datasets)
    const isSimple = values && !datasets;
    const isStacked = chart_type === 'stacked-bar';

    if (isSimple) {
      // Simple bar chart with single dataset
      const validatedLabels = validateArray(labels, 'labels');
      if (!validatedLabels) return null;

      const validatedValues = validateArray(values, 'values');
      if (!validatedValues) return null;

      if (validatedValues.length !== validatedLabels.length) {
        console.error(
          `Bar chart length mismatch: ${validatedLabels.length} labels vs ${validatedValues.length} values`
        );
        return null;
      }

      const chartData = validatedLabels.map((label: string, index: number) => ({
        category: label,
        value: validatedValues[index],
        fill: (colors && colors[index]) || getDefaultColor(index)
      }));

      return (
        <div className="my-4">
          <h4 className="text-base font-semibold mb-2 text-gray-800">{title}</h4>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    } else {
      // Grouped or stacked bar chart with multiple datasets
      const validatedLabels = validateArray(labels, 'labels');
      if (!validatedLabels) return null;

      const validatedDatasets = validateDatasets(datasets, ['name', 'values']);
      if (!validatedDatasets) return null;

      // Validate dataset values match labels
      for (let i = 0; i < validatedDatasets.length; i++) {
        const dataset = validatedDatasets[i];
        const dsValues = validateArray(dataset.values, `dataset[${i}].values`);
        if (!dsValues) return null;
        
        if (dsValues.length !== validatedLabels.length) {
          console.error(
            `Dataset ${i} length mismatch: ${dsValues.length} values vs ${validatedLabels.length} labels`
          );
          return null;
        }
      }

      const chartData = validatedLabels.map((label: string, index: number) => {
        const point: any = { category: label };
        validatedDatasets.forEach((dataset: any) => {
          point[dataset.name] = dataset.values[index];
        });
        return point;
      });

      return (
        <div className="my-4">
          <h4 className="text-base font-semibold mb-2 text-gray-800">{title}</h4>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="category" />
              <YAxis />
              <Tooltip />
              <Legend />
              {validatedDatasets.map((dataset: any, index: number) => (
                <Bar
                  key={index}
                  dataKey={dataset.name}
                  fill={dataset.color || getDefaultColor(index)}
                  stackId={isStacked ? "stack" : undefined}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    }
  } catch (error) {
    console.error('BarChart rendering error:', error, data);
    return null;
  }
};

// Line Chart Renderer
const LineChartRenderer: React.FC<{ data: any }> = ({ data }) => {
  try {
    const { title = 'Line Chart', labels, datasets } = data;

    // Validate required fields
    const validatedLabels = validateArray(labels, 'labels');
    if (!validatedLabels) return null;

    const validatedDatasets = validateDatasets(datasets, ['name', 'values']);
    if (!validatedDatasets) return null;

    // Validate dataset values match labels
    for (let i = 0; i < validatedDatasets.length; i++) {
      const dataset = validatedDatasets[i];
      const values = validateArray(dataset.values, `dataset[${i}].values`);
      if (!values) return null;
      
      if (values.length !== validatedLabels.length) {
        console.error(
          `Dataset ${i} length mismatch: ${values.length} values vs ${validatedLabels.length} labels`
        );
        return null;
      }
    }

    // Transform data for Recharts
    const chartData = validatedLabels.map((label: string, index: number) => {
      const point: any = { category: label };
      validatedDatasets.forEach((dataset: any) => {
        point[dataset.name] = dataset.values[index];
      });
      return point;
    });

    return (
      <div className="my-4">
        <h4 className="text-base font-semibold mb-2 text-gray-800">{title}</h4>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="category" />
            <YAxis />
            <Tooltip />
            <Legend />
            {validatedDatasets.map((dataset: any, index: number) => (
              <Line
                key={index}
                type="monotone"
                dataKey={dataset.name}
                stroke={dataset.color || getDefaultColor(index)}
                strokeWidth={2}
                dot={{ fill: dataset.color || getDefaultColor(index), r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  } catch (error) {
    console.error('LineChart rendering error:', error, data);
    return null;
  }
};

// Pie Chart Renderer
const PieChartRenderer: React.FC<{ data: any }> = ({ data }) => {
  try {
    const { title = 'Pie Chart', labels, values, colors } = data;

    // Validate required fields exist
    const validatedLabels = validateArray(labels, 'labels');
    if (!validatedLabels) {
      console.error('PieChart: labels validation failed');
      return null;
    }

    const validatedValues = validateArray(values, 'values');
    if (!validatedValues) {
      console.error('PieChart: values validation failed');
      return null;
    }

    // Critical check: lengths must match
    if (validatedValues.length !== validatedLabels.length) {
      console.error(
        `PieChart length mismatch: ${validatedLabels.length} labels vs ${validatedValues.length} values`
      );
      return null;
    }

    // Build chart data with validation
    const chartData: Array<{ name: string; value: number }> = [];
    for (let i = 0; i < validatedLabels.length; i++) {
      const label = validatedLabels[i];
      const value = validatedValues[i];
      
      // Validate each data point
      if (label === null || label === undefined) {
        console.error(`PieChart: label at index ${i} is null/undefined`);
        return null;
      }
      
      if (value === null || value === undefined || isNaN(Number(value))) {
        console.error(`PieChart: value at index ${i} is invalid: ${value}`);
        return null;
      }
      
      chartData.push({
        name: String(label),
        value: Number(value)
      });
    }

    // Ensure we have valid data to render
    if (chartData.length === 0) {
      console.error('PieChart: no valid data points after validation');
      return null;
    }

    // Default colors if not provided
    const COLORS = colors && Array.isArray(colors) && colors.length >= chartData.length
      ? colors
      : ['#2196f3', '#4caf50', '#ffc107', '#f44336', '#9c27b0', '#009688', '#ff5722', '#795548', '#607d8b', '#e91e63'];

    return (
      <div className="my-4">
        <h4 className="text-base font-semibold mb-2 text-gray-800">{title}</h4>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  } catch (error) {
    console.error('PieChart rendering error:', error, data);
    return null;
  }
};

// // Heatmap Renderer - Horizontal bars with percentages (UMD Colors)
// const HeatmapRenderer: React.FC<{ data: any }> = ({ data }) => {
//   try {
//     const { title = 'Heatmap', rows, columns, data: heatmapData } = data;
//
//     // Validate required fields
//     const validatedRows = validateArray(rows, 'rows');
//     if (!validatedRows) return null;
//
//     const validatedColumns = validateArray(columns, 'columns');
//     if (!validatedColumns) return null;
//
//     const validatedData = validateArray(heatmapData, 'data');
//     if (!validatedData) return null;
//
//     // Validate data dimensions match rows
//     if (validatedData.length !== validatedRows.length) {
//       console.error(
//         `Heatmap data length mismatch: ${validatedData.length} rows vs ${validatedRows.length} row labels`
//       );
//       return null;
//     }
//
//     // Validate each row has correct number of columns
//     for (let i = 0; i < validatedData.length; i++) {
//       const row = validatedData[i];
//       if (!Array.isArray(row)) {
//         console.error(`Heatmap row ${i} is not an array`);
//         return null;
//       }
//       if (row.length !== validatedColumns.length) {
//         console.error(
//           `Heatmap row ${i} length mismatch: ${row.length} values vs ${validatedColumns.length} columns`
//         );
//         return null;
//       }
//     }
//
//     // UMD Colors with intensity variations
//     const getBarColor = (value: number, columnIndex: number) => {
//       const percentage = Math.min(Math.max(value, 0), 100);
//
//       if (percentage === 0) {
//         return '#f3f4f6'; // Light gray for 0%
//       }
//
//       // UMD Color schemes
//       const colorSchemes = [
//         { base: [226, 24, 51], name: 'umd-red' },    // #E21833 UMD Red for first exam
//         { base: [0, 0, 0], name: 'black' },          // #000000 Black for second exam
//         { base: [88, 89, 91], name: 'gray' }         // #58595B Gray for third exam
//       ];
//
//       const scheme = colorSchemes[columnIndex % colorSchemes.length];
//
//       // Calculate opacity based on percentage (50% to 100% range)
//       const intensity = 0.5 + (percentage / 200);
//
//       return `rgba(${scheme.base[0]}, ${scheme.base[1]}, ${scheme.base[2]}, ${intensity})`;
//     };
//
//     // UMD Colors for headers
//     const getHeaderColor = (columnIndex: number) => {
//       const colors = ['#E21833', '#000000', '#58595B']; // UMD Red, Black, Gray
//       return colors[columnIndex % colors.length];
//     };
//
//     return (
//       <div className="my-4 bg-white rounded-lg">
//         <h4 className="text-base font-semibold mb-3 text-gray-800">{title}</h4>
//
//         {/* Column headers - Side by side */}
//         <div className="mb-3 flex gap-1 items-center">
//           <div className="w-44"></div>
//           {validatedColumns.map((col: string, idx: number) => (
//             <div key={idx} className="flex-1 text-center max-w-md">
//               <span
//                 className="text-base font-bold"
//                 style={{ color: getHeaderColor(idx) }}
//               >
//                 {col}
//               </span>
//             </div>
//           ))}
//         </div>
//
//         {/* Data rows */}
//         <div className="space-y-1.5">
//           {validatedRows.map((row: string, rowIdx: number) => (
//             <div key={rowIdx} className="flex gap-1 items-center py-1">
//               {/* Topic name */}
//               <div className="w-44 text-right pr-3">
//                 <span className="text-xs font-medium text-gray-700">{row}</span>
//               </div>
//
//               {/* Percentage bars for each exam */}
//               {validatedData[rowIdx].map((value: number, colIdx: number) => (
//                 <div key={colIdx} className="flex-1 max-w-md">
//                   <div
//                     className="h-7 rounded flex items-center justify-center transition-all duration-300"
//                     style={{
//                       backgroundColor: getBarColor(value, colIdx),
//                       width: `${Math.min(Math.max(value, 0), 100)}%`,
//                       minWidth: value > 0 ? '60px' : '0'
//                     }}
//                   >
//                     {value > 0 && (
//                       <span className="text-xs font-bold text-white">
//                         {value}%
//                       </span>
//                     )}
//                   </div>
//                 </div>
//               ))}
//             </div>
//           ))}
//         </div>
//       </div>
//     );
//   } catch (error) {
//     console.error('Heatmap rendering error:', error, data);
//     return null;
//   }
// };
